import { useState } from 'react';
import { User, Calculator, Calendar, Plus } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { CampaignFinancialSummary } from '../CampaignFinancialSummary';
import { QuickClientModal } from '../QuickClientModal';
import type { CampaignCalculationResult } from '../../hooks/useCampaignCalculations';
import { FormBlock } from '../ui/FormBlock';

interface GeneralInfoTabProps {
  formData: {
    year: number;
    work_code: string;
    client: string;
    brand: string;
    campaign_ref: string;
    agent_name: string;
    campaign_budget: number;
    admin_only?: boolean;
  };
  clients: string[];
  isEditing: boolean;
  agentName: string;
  calculations: CampaignCalculationResult;
  influencersCount: number;
  onChange: (field: string, value: any) => void;
  onClientCreated?: (clientName: string) => void;
}

export function GeneralInfoTab({
  formData,
  clients,
  isEditing,
  agentName,
  calculations,
  influencersCount,
  onChange,
  onClientCreated,
}: GeneralInfoTabProps) {
  const { t } = useLanguage();
  const [showClientModal, setShowClientModal] = useState(false);
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  const clientIsNew = formData.client.trim() !== '' &&
    !clients.some(c => c.toLowerCase() === formData.client.trim().toLowerCase());

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    onChange(name, type === 'number' ? parseFloat(value) || 0 : value);
  };

  const handleClientCreated = (clientName: string) => {
    onChange('client', clientName);
    setShowClientModal(false);
    onClientCreated?.(clientName);
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(amount);

  return (
    <div className="p-5 sm:p-6 space-y-6">
      {/* Base info block */}
      <FormBlock title="Informação base">
        <div>
          <div className="space-y-8">

            {/* Identificação */}
            <div>
              <p className="cm-caption">Identificação</p>
              <div className="cm-grid grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">

                <div className="cm-field">
                  <label className="cm-label">Agente</label>
                  <div className="cm-readonly flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-500" />
                    <span>{agentName}</span>
                  </div>
                </div>

                <div className="cm-field">
                  <label className="cm-label">Ano <span className="cm-req">*</span></label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                    <select
                      name="year"
                      value={formData.year}
                      onChange={(e) => onChange('year', parseInt(e.target.value))}
                      required
                      className="cm-select pl-9"
                    >
                      {years.map((year) => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="cm-field">
                  <label className="cm-label">{t('form.campaign.workCode')} <span className="cm-req">*</span></label>
                  <input
                    type="text"
                    name="work_code"
                      data-required-field="im-work-code"
                    value={formData.work_code}
                    onChange={handleChange}
                    required
                    disabled={!isEditing}
                    placeholder={isEditing ? '' : 'A gerar…'}
                    className="cm-input"
                  />
                  {!isEditing && (
                    <p className="cm-help">{t('form.workCodeAutoGenerated')}</p>
                  )}
                </div>

                <div className="cm-field">
                  <label className="cm-label">Marca <span className="cm-req">*</span></label>
                  <input
                    type="text"
                    name="brand"
                    value={formData.brand}
                    onChange={handleChange}
                    required
                    placeholder="Escolher marca…"
                    className="cm-input"
                  />
                </div>

                <div className="cm-field md:col-span-2">
                  <label className="cm-label">Cliente <span className="cm-req">*</span></label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      name="client"
                      data-required-field="im-client"
                      list="clients-list"
                      value={formData.client}
                      onChange={handleChange}
                      required
                      placeholder="Escolher ou escrever o nome do cliente…"
                      className="cm-input flex-1"
                    />
                    <datalist id="clients-list">
                      {clients.map((client) => (
                        <option key={client} value={client} />
                      ))}
                    </datalist>
                    <button
                      type="button"
                      onClick={() => setShowClientModal(true)}
                      className="px-3 py-2 bg-accent-50 border border-accent-100 text-accent-700 rounded-lg hover:bg-accent-100 transition-colors flex items-center gap-1.5 text-sm font-medium"
                      title="Create new client"
                    >
                      <Plus className="w-4 h-4" />
                      Novo
                    </button>
                  </div>
                  {clientIsNew && (
                    <p className="text-xs text-amber-600 mt-1.5 flex items-center gap-1">
                      Client not found in database --
                      <button
                        type="button"
                        onClick={() => setShowClientModal(true)}
                        className="underline hover:text-amber-700 font-medium"
                      >
                        create now with full details
                      </button>
                    </p>
                  )}
                </div>

                <div className="cm-field">
                  <label className="cm-label">Referência da Campanha</label>
                  <input
                    type="text"
                    name="campaign_ref"
                    value={formData.campaign_ref}
                    onChange={handleChange}
                    placeholder="Referência da Campanha"
                    className="cm-input"
                  />
                </div>

              </div>
            </div>

            {/* Financeiro */}
            <div>
              <p className="cm-caption">Financeiro</p>
              <div className="cm-grid grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">

                <div className="cm-field">
                  <label className="cm-label">{t('financial.campaignBudget')}</label>
                  <input
                    type="number"
                    name="campaign_budget"
                    value={formData.campaign_budget || ''}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    className="cm-input"
                  />
                  <p className="cm-help">{t('financial.commissionOnlyDescription')}</p>
                </div>

                <div className="cm-field">
                  <label className="cm-label">
                    {t('financial.influencerFees')} + {t('financial.serviceCosts')}
                  </label>
                  <div className="cm-readonly font-medium">
                    {formatCurrency(calculations.totalCosts)}
                  </div>
                  <p className="cm-help">
                    {formatCurrency(calculations.totalInfluencerCosts)} + {formatCurrency(calculations.totalServiceCosts)}
                  </p>
                </div>

                <div className="cm-field md:col-span-2">
                  <label className="cm-label">
                    {calculations.isCommissionOnlyMode ? t('financial.totalRevenue') : t('financial.totalMargin')}
                  </label>
                  <div
                    className={`cm-readonly font-semibold ${
                      calculations.marginIsPositive ? 'cm-readonly--accent' : 'bg-red-50 border-red-300 text-red-700'
                    }`}
                  >
                    {formatCurrency(calculations.operationalMargin)}
                  </div>
                  <p className="cm-help">
                    {calculations.isCommissionOnlyMode
                      ? t('financial.formulaCommissionOnly')
                      : t('financial.formulaStandard')}
                  </p>
                </div>

                <div className="cm-field md:col-span-2">
                  <CampaignFinancialSummary
                    calculations={calculations}
                    clientBudget={formData.campaign_budget}
                    influencersCount={influencersCount}
                  />
                </div>

              </div>
            </div>

          </div>
        </div>
      </FormBlock>

      {showClientModal && (
        <QuickClientModal
          initialName={formData.client}
          onClose={() => setShowClientModal(false)}
          onCreated={handleClientCreated}
        />
      )}
    </div>
  );
}
